// Generic entity accessors — the client half of the entity registry.
//
// Paths, defaults, and writable fields all come from entityRegistry.gen.json
// (generated from backend/shared/entities/registry.js — run
// `node scripts/generate-entities.mjs` after registry edits; never edit the
// JSON by hand). Adding an entity to the registry makes it usable here with
// zero new client code:
//
//   const { docs: assets } = useEntityList('asset', { item: itemId });
//   await createEntity('asset', { item: itemId }, { name, s3Key });
//
// Scope objects name ancestor ids by entity: { item: 'i1' }.
//
// The hand-written demo accessors in data/firestore.js still exist (they
// carry demo-specific logic like counter increments); NEW domains should
// start here and only hand-write what the registry can't express.
import { useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import registry from './entityRegistry.gen.json';

const ready = () => firebase.apps.length > 0 && db;

export const ENTITIES = registry.entities;
export const ACCESS_ALLOWLIST = registry.accessAllowlist;

function parentChain(entityName) {
  const chain = [];
  let cur = ENTITIES[entityName]?.parent;
  while (cur) {
    chain.unshift(cur);
    cur = ENTITIES[cur]?.parent;
  }
  return chain;
}

export function collectionPath(entityName, scope = {}) {
  const def = ENTITIES[entityName];
  if (!def) throw new Error(`unknown entity: ${entityName}`);
  const segs = [];
  for (const anc of parentChain(entityName)) {
    if (!scope[anc]) throw new Error(`missing scope id for '${anc}' (entity ${entityName})`);
    segs.push(ENTITIES[anc].collection, String(scope[anc]));
  }
  segs.push(def.collection);
  return segs.join('/');
}

export const docPath = (entityName, scope, id) => `${collectionPath(entityName, scope)}/${id}`;

// Strip server-only keys and apply declared defaults for a create payload.
function prepareCreate(entityName, payload) {
  const def = ENTITIES[entityName];
  const out = {};
  for (const [key, f] of Object.entries(def.fields)) {
    if (f.serverOnly) continue;
    if (payload[key] !== undefined && payload[key] !== null) out[key] = payload[key];
    else if (f.default !== undefined) out[key] = f.default;
  }
  if (def.fields.createdAt) out.createdAt = out.createdAt || Date.now();
  return out;
}

// ---- Live hooks ---------------------------------------------------------------
export function useEntityList(entityName, scope, { orderBy = 'createdAt', dir = 'desc', enabled = true, where = null } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});
  const whereKey = JSON.stringify(where || null);

  useEffect(() => {
    if (!ready() || !enabled) {
      setDocs(null);
      return undefined;
    }
    let path;
    try {
      path = collectionPath(entityName, JSON.parse(scopeKey));
    } catch {
      setDocs(null); // scope incomplete (e.g. still loading an ancestor id)
      return undefined;
    }
    let ref = db.collection(path);
    // Optional filters: [[field, op, value], …]. NB: combining a filter with
    // orderBy on a different field needs a composite index — callers using
    // `where` typically sort client-side and pass orderBy: null.
    for (const [f, op, v] of JSON.parse(whereKey) || []) ref = ref.where(f, op, v);
    if (orderBy) ref = ref.orderBy(orderBy, dir);
    const unsub = ref.onSnapshot(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        setError(err.message);
        setDocs([]);
      }
    );
    return () => unsub();
  }, [entityName, scopeKey, orderBy, dir, enabled, whereKey]);

  return { docs, error };
}

export function useEntityDoc(entityName, scope, id) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});

  useEffect(() => {
    if (!ready() || !id) {
      setDoc(undefined);
      return undefined;
    }
    let path;
    try {
      path = docPath(entityName, JSON.parse(scopeKey), id);
    } catch {
      setDoc(undefined);
      return undefined;
    }
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [entityName, scopeKey, id]);

  return { doc, error };
}

// ---- CRUD -----------------------------------------------------------------------
export async function createEntity(entityName, scope, payload, { id = null } = {}) {
  const col = db.collection(collectionPath(entityName, scope));
  const ref = id ? col.doc(String(id)) : col.doc();
  await ref.set(prepareCreate(entityName, payload));
  return ref.id;
}

export async function updateEntity(entityName, scope, id, patch) {
  await db.doc(docPath(entityName, scope, id)).set(
    { ...patch, updatedAt: Date.now() },
    { merge: true }
  );
}

export async function deleteEntity(entityName, scope, id) {
  await db.doc(docPath(entityName, scope, id)).delete();
}
